
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getChatRooms } from "@/hooks/useChatRooms";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Database } from "@/integrations/supabase/types";
import { Users, MessageSquareMore, Sparkles, Network } from "lucide-react";
import CreateChatRoomForm from "@/components/CreateChatRoomForm";

type ChatRoom = Database['public']['Tables']['chat_rooms']['Row'];

export default function ChatRooms() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Fetching chat rooms...");
        
        const { data, error } = await getChatRooms();
        
        console.log("Chat rooms fetch result:", { data, error });
        
        if (error) {
          console.error("Error fetching chat rooms:", error);
          setError(`Failed to fetch chat rooms: ${error.message}`);
        } else {
          console.log("Successfully fetched rooms:", data);
          setRooms(data || []);
        }
      } catch (err) {
        console.error("Unexpected error fetching chat rooms:", err);
        setError(`Unexpected error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
  }, []);

  const handleRoomClick = (roomId: string) => {
    navigate(`/chat-rooms/${roomId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-anyskill-purple via-anyskill-blue to-anyskill-orange">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
            <p className="text-white text-lg font-medium">Loading amazing chat rooms...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-anyskill-purple via-anyskill-blue to-anyskill-orange">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-md mx-auto">
              <p className="text-white text-lg mb-4">Error loading chat rooms:</p>
              <p className="text-white/90 text-sm font-mono bg-white/20 p-3 rounded">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-white/20 text-white rounded hover:bg-white/30 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-anyskill-purple via-anyskill-blue to-anyskill-orange relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-4 -left-4 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-4 w-96 h-96 bg-anyskill-orange/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute -bottom-4 left-1/3 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Network className="h-12 w-12 text-white animate-bounce" />
            <Sparkles className="h-8 w-8 text-white animate-pulse" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-white to-white bg-clip-text text-transparent mb-4 font-poppins drop-shadow-lg">
            Chat Rooms 🚀
          </h1>
          <p className="text-xl text-white/90 font-inter drop-shadow-sm max-w-2xl mx-auto">
            Connect with entrepreneurs, service providers, and clients to supercharge your projects. Share insights, find collaborators, and grow your network! ✨
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-1 shadow-2xl hover:shadow-3xl transition-all duration-300">
              <div className="bg-card rounded-xl">
                <CreateChatRoomForm />
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-2">
            {rooms.length === 0 ? (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-2xl">
                <div className="text-center">
                  <div className="bg-white/20 rounded-full p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                    <Users className="h-12 w-12 text-white" />
                  </div>
                  <p className="text-white text-xl font-medium mb-2">No chat rooms yet!</p>
                  <p className="text-white/80">Be the first to create an amazing conversation space 🎯</p>
                </div>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {rooms.map((room, index) => (
                  <div
                    key={room.id}
                    className="group bg-white/10 backdrop-blur-sm rounded-2xl p-1 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 cursor-pointer animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => handleRoomClick(room.id)}
                  >
                    <Card className="bg-card rounded-xl border-0 overflow-hidden group-hover:bg-gradient-to-br group-hover:from-card group-hover:to-card/95 transition-all duration-300">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="relative">
                              <Avatar className="h-12 w-12 ring-2 ring-anyskill-purple/20 group-hover:ring-anyskill-purple/40 transition-all duration-300">
                                <AvatarFallback className="bg-gradient-to-br from-anyskill-purple to-anyskill-blue text-white font-bold text-lg">
                                  {room.name.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-anyskill-orange to-anyskill-purple rounded-full animate-pulse"></div>
                            </div>
                            <div>
                              <CardTitle className="text-lg font-bold bg-gradient-to-r from-anyskill-purple to-anyskill-blue bg-clip-text text-transparent group-hover:from-anyskill-blue group-hover:to-anyskill-orange transition-all duration-300">
                                {room.name}
                              </CardTitle>
                              {room.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {room.description}
                                </p>
                              )}
                            </div>
                          </div>
                          {room.is_private && (
                            <Badge className="bg-gradient-to-r from-anyskill-purple to-anyskill-blue text-white border-0 shadow-lg">
                              Private ✨
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span className="flex items-center space-x-1">
                            <span>Created {new Date(room.created_at || '').toLocaleDateString()}</span>
                          </span>
                          <div className="flex items-center space-x-1">
                            <Users className="h-4 w-4 text-anyskill-blue" />
                            <Network className="h-4 w-4 text-anyskill-purple" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
